import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {marker} from '@biesbjerg/ngx-translate-extract-marker';
import {TranslateService} from '@ngx-translate/core';
import {catchError, map} from 'rxjs/operators';
import {isNil} from "lodash-es";

@Injectable({
  providedIn: 'root',
})
export class LabelingService {

  // https://dev-api.mobileinsight.com/api/organizations/3/labels/en
  private apiUrl = environment.apiUrl;
  private currentLang = this.readStorage('language') || 'en';
  private orgId;

  private regionLabelSubject: BehaviorSubject<string> = new BehaviorSubject<string>( 'Region');
  private regionLabel$ = this.regionLabelSubject.asObservable();
  private divisionLabelSubject: BehaviorSubject<string> = new BehaviorSubject<string>('Division');
  private divisionLabel$ = this.divisionLabelSubject.asObservable();
  private clusterLabelSubject: BehaviorSubject<string> = new BehaviorSubject<string>('Cluster');
  private clusterLabel$ = this.clusterLabelSubject.asObservable();
  private storeDesignationLabelSubject: BehaviorSubject<string> = new BehaviorSubject<string>('Store Designation');
  private storeDesignationLabel$ = this.storeDesignationLabelSubject.asObservable();
  private storeDesignationsLabelSubject: BehaviorSubject<string> = new BehaviorSubject<string>('Store Designations');
  private storeDesignationsLabel$ = this.storeDesignationsLabelSubject.asObservable();
  private storeTypeLabelSubject: BehaviorSubject<string> = new BehaviorSubject<string>('Store Type');
  private storeTypeLabel$ = this.storeTypeLabelSubject.asObservable();
  private storeTypesLabelSubject: BehaviorSubject<string> = new BehaviorSubject<string>('Store Types');
  private storeTypesLabel$ = this.storeTypesLabelSubject.asObservable();
  private storeLabelSubject: BehaviorSubject<string> = new BehaviorSubject<string>('Store');
  private storeLabel$ = this.storeLabelSubject.asObservable();
  private accountLabelSubject: BehaviorSubject<string> = new BehaviorSubject<string>('Account');
  private accountLabel$ = this.accountLabelSubject.asObservable();
  private accountsLabelSubject: BehaviorSubject<string> = new BehaviorSubject<string>('Accounts');
  private accountsLabel$ = this.accountsLabelSubject.asObservable();
  public languageSubject: BehaviorSubject<string> = new BehaviorSubject<string>( this.currentLang);

  private storesLabelSubject: BehaviorSubject<string> = new BehaviorSubject<string>('Stores');
  private storesLabel$ = this.storesLabelSubject.asObservable();

  public language$ = this.languageSubject.asObservable();


  private userDynamicLabels = [USER_DYNAMIC_LABEL.DIVISION, USER_DYNAMIC_LABEL.CLUSTER, USER_DYNAMIC_LABEL.REGION];
  private emptyServerLabels: boolean;


  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
  ) {
    this.orgId = this.readStorage('selectedOrganization');

    this.emitNewLabels();
  }

  emitNewLabels() {
    const rawLabels = this.readStorage('labels');
    const labels = this.parseLabels(rawLabels);
    this.emptyServerLabels = isNil(labels) || Object.keys(labels).length === 0;
    this.emitOnSubjects(labels, null);
  }

  languageChanged(newLang: string): void {
    this.currentLang = newLang;
    this.writeStorage('language', newLang);
    this.reloadLabels();
    this.languageSubject.next(newLang);
  }

  orgChanged(newOrgId: string): void {
    if (newOrgId === undefined || newOrgId === null) {
      return;
    }

    this.orgId = newOrgId;
    this.reloadLabels();
  }

  private reloadLabels(): void {
    const labels$ = this.getServerLabels();
    let subscription: Subscription;
    subscription = labels$.subscribe(labels => {
      this.emptyServerLabels = isNil(labels) || Object.keys(labels).length === 0;
      this.writeStorage('labels', JSON.stringify(labels));
      this.emitOnSubjects(labels, subscription);
    },
      error => console.log('Error receiving labels'),
      );
  }

  getRegionLabel(): Observable<string> {
    return combineLatest([
      this.regionLabel$,
    ]).pipe(map(([data1]) => `${data1}`));
  }

  getDivisionLabel(): Observable<string> {
    return combineLatest([
      this.divisionLabel$,
    ]).pipe(map(([data1]) => `${data1}`));
  }

  getClusterLabel(): Observable<string> {
    return combineLatest([
      this.clusterLabel$,
    ]).pipe(map(([data1]) => `${data1}`));
  }

  getStoreDivision() {
    return combineLatest([
      this.storeLabel$,
      this.divisionLabel$,
    ]).pipe(map(([data1, data2]) => `${data1} ${data2}`));
  }

  getStoreCluster() {
    return combineLatest([
      this.storeLabel$,
      this.clusterLabel$,
    ]).pipe(map(([data1, data2]) => `${data1} ${data2}`));
  }

  getStoreRegion() {
    return combineLatest([
      this.storeLabel$,
      this.regionLabel$,
    ]).pipe(map(([data1, data2]) => `${data1} ${data2}`));
  }

  getManagerDivision() {
    return this.getDivisionSentenceLabel(marker('Manager\'s Division'), marker('Manager\'s'));
  }

  getManagerCluster() {
    return this.getClusterSentenceLabel(marker('Manager\'s Cluster'), marker('Manager\'s'));
  }

  getManagerRegion() {
    return this.getRegionSentenceLabel(marker('Manager\'s Region'), marker('Manager\'s'));
  }

  getStoreDesignationLabel() {
    return combineLatest([
      this.storeDesignationLabel$,
    ]);
  }

  getStoreDesignationsLabel() {
    return combineLatest([
      this.storeDesignationsLabel$,
    ]);
  }

  getStoreDesignationRequired() {
    return this.getStoreDesignationSentenceLabel(marker('Store Designation is required'), marker('is required'));
  }

  getStoreTypeLabel() {
    return combineLatest([
      this.storeTypeLabel$,
    ]);
  }

  getStoreTypesLabel() {
    return combineLatest([
      this.storeTypesLabel$,
    ]);
  }

  getStoreTypeRequired() {
    return this.getStoreTypeSentenceLabel(marker('Store Type is required'), marker('is required'));
  }

  getAccountLabel() {
    return combineLatest([
      this.accountLabel$,
    ]);
  }

  getAccountsLabel() {
    return combineLatest([
      this.accountsLabel$,
    ]);
  }

  getAccountName() {
    return this.getAccountSentenceLabel(marker('Account Name'), marker('Name'));
  }

  getAccountRequired() {
    return this.getAccountSentenceLabel(marker('Account is required'), marker('is required'));
  }

  getStoreLabel() {
    return combineLatest([
      this.storeLabel$,
    ]);
  }

  getStoresLabel() {
    return combineLatest([
      this.storesLabel$,
    ]);
  }

  getNicknameLabel() {
    return combineLatest([
      this.language$
    ])
    .pipe(
      map( _ => {
        let response = '';
        this.translateService.stream(marker('Nickname')).subscribe(txt => response = txt);
        return response;
      }));
  }

  getNicknameExistsLabel() {
    return combineLatest([
      this.translateService.stream(marker('Nickname already exists')),
      this.language$
    ]).pipe(map(([data1, _]) => `${data1}`));
  }


  getStoreCSVIsRequiredLabel() {
    return this.getStoreSentenceLabel(marker('Store CSV is required'), marker('CSV is required'));
  }

  //additionalTranslateMarkers are used only to define translate markers, not in the body of this function
  getStoreSentenceLabel(sentence: string, ...additionalTranslateMarkers: string[]) {
    return this.getDynamicLabelSentence(sentence, 'Store', this.storeLabel$);
  }

  getStoresSentenceLabel(sentence: string, ...additionalTranslateMarkers: string[]) {
    return this.getDynamicLabelSentence(sentence, 'Stores', this.storesLabel$);
  }

  getAccountSentenceLabel(sentence: string, ...additionalTranslateMarkers: string[]) {
    return this.getDynamicLabelSentence(sentence, 'Account', this.accountLabel$);
  }

  getStoreTypeSentenceLabel(sentence: string, ...additionalTranslateMarkers: string[]) {
    return this.getDynamicLabelSentence(sentence, 'Store Type', this.storeTypeLabel$);
  }

  getStoreTypesSentenceLabel(sentence: string, ...additionalTranslateMarkers: string[]) {
    return this.getDynamicLabelSentence(sentence, 'Store Types', this.storeTypesLabel$);
  }


  getStoreDesignationSentenceLabel(sentence: string, ...additionalTranslateMarkers: string[]) {
    return this.getDynamicLabelSentence(sentence, 'Store Designation', this.storeDesignationLabel$);
  }

  getStoreDesignationsSentenceLabel(sentence: string, ...additionalTranslateMarkers: string[]) {
    return this.getDynamicLabelSentence(sentence, 'Store Designations', this.storeDesignationLabel$);
  }

  getDivisionSentenceLabel(sentence: string, ...additionalTranslateMarkers: string[]) {
    return this.getDynamicLabelSentence(sentence, 'Division', this.divisionLabel$);
  }

  getClusterSentenceLabel(sentence: string, ...additionalTranslateMarkers: string[]) {
    return this.getDynamicLabelSentence(sentence, 'Cluster', this.clusterLabel$);
  }

  getRegionSentenceLabel(sentence: string, ...additionalTranslateMarkers: string[]) {
    return this.getDynamicLabelSentence(sentence, 'Region', this.regionLabel$);
  }


  getDynamicLabelSentence(sentence: string, dynamicLabel: string, dynamicLabelObs$: Observable<any>) {
    if (this.emptyServerLabels) {
      return this.translateService.stream(sentence);
    }

    if (sentence.startsWith(dynamicLabel + ' ')) {
      const suffix = sentence.substring((dynamicLabel + ' ').length, sentence.length);
      return combineLatest([
        dynamicLabelObs$,
        this.language$
      ]).pipe(map(([dynamicLabelValue, _]) => {
        let translated = '';
        this.translateService.stream(suffix).subscribe(txt => translated = txt);
        return dynamicLabelValue + ' ' + translated ;
      }));
    } else if (sentence.endsWith(' ' + dynamicLabel)) {
      const storeIndex = sentence.indexOf(' ' + dynamicLabel);
      const prefix = sentence.substring(0, storeIndex);
      return combineLatest([
        dynamicLabelObs$,
        this.language$
      ]).pipe(map(([dynamicLabelValue, _]) => {
        let translated = '';
        this.translateService.stream(prefix).subscribe(txt => translated = txt);
        return translated + ' ' + dynamicLabelValue;
      }));
    } else if (sentence.includes(' ' + dynamicLabel + ' ')) {
      //in the middle
      const storeIndex = sentence.indexOf(' ' + dynamicLabel + ' ');
      const prefix = sentence.substring(0, storeIndex);
      const suffix = sentence.substring(storeIndex + (' ' + dynamicLabel + ' ').length, sentence.length);
      return combineLatest([
        dynamicLabelObs$,
        this.language$
      ]).pipe(map(([dynamicLabelValue, _]) => {
        let translatedPrefix = '', translatedSuffix = '';
        this.translateService.stream(prefix).subscribe(txt => translatedPrefix = txt);
        this.translateService.stream(suffix).subscribe(txt => translatedSuffix = txt);
        return translatedPrefix + ' ' + dynamicLabelValue + ' ' + translatedSuffix;
      }));
    } else {
      return this.translateService.stream(sentence);
    }
  }


  private emitOnSubjects(serverLabels: any, subscription: Subscription): void {

    this.emitLabel(this.regionLabelSubject, serverLabels, 'regionUpperCaseSingular', marker('Region'));
    this.emitLabel(this.divisionLabelSubject, serverLabels, 'divisionUpperCaseSingular', marker('Division'));
    this.emitLabel(this.clusterLabelSubject, serverLabels, 'clusterUpperCaseSingular', marker('Cluster'));
    this.emitLabel(this.storeDesignationLabelSubject, serverLabels, 'storeDesignationUpperCaseSingular', marker('Store Designation'));
    this.emitLabel(this.storeTypeLabelSubject, serverLabels, 'storeTypeUpperCaseSingular', marker('Store Type'));
    this.emitLabel(this.storeLabelSubject, serverLabels, 'storeUpperCaseSingular', marker('Store'));
    this.emitLabel(this.accountLabelSubject, serverLabels, 'accountUpperCaseSingular', marker('Account'));

    this.emitLabel(this.accountsLabelSubject, serverLabels, 'accountUpperCasePlural', marker('Accounts'));
    this.emitLabel(this.storesLabelSubject, serverLabels, 'storeUpperCasePlural', marker('Stores'));
    this.emitLabel(this.storeDesignationsLabelSubject, serverLabels,
      'storeDesignationUpperCasePlural', marker('Store Designations'));
    this.emitLabel(this.storeTypesLabelSubject, serverLabels, 'storeTypeUpperCasePlural', marker('Store Types'));

    if (subscription) {
      subscription.unsubscribe();
    }
  }

  private emitLabel(subject: BehaviorSubject<string>, labels: any, serverKey: string, localKey: string): void {
    if (labels && labels[serverKey] !== undefined) {
      subject.next(labels[serverKey]);
    } else {
      // publish regular translation
      this.translateService.stream(localKey).subscribe(localTranslation => {
        subject.next(localTranslation);
      });
    }
  }

  labelsServerUrl(): string {
    return this.apiUrl + 'organizations/' + encodeURIComponent(this.orgId) + '/labels/' + encodeURIComponent(this.currentLang);
  }

  getServerLabels(): Observable<any> {
    if (this.orgId === undefined || this.orgId === null) {
      return of({});
    }

    const url = this.labelsServerUrl();

    return this.http.get<any>(url).pipe(
      catchError(error => {
        console.log('Error receiving labels');
        return of({});
      }),
    );
  }

  private readStorage(key: string): string | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private writeStorage(key: string, value: string): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    try {
      localStorage.setItem(key, value);
    } catch {
      // Ignore storage write failures in SSR/private browsing contexts.
    }
  }

  private parseLabels(rawLabels: string | null): any {
    if (!rawLabels) {
      return {};
    }
    try {
      return JSON.parse(rawLabels);
    } catch {
      return {};
    }
  }

  isUserDynamicLabel(fieldName: string) {
    return this.userDynamicLabels.includes(<USER_DYNAMIC_LABEL>fieldName);
  }

  getUserDynamicLabel(fieldName: string): Observable<any> {
      if (fieldName === USER_DYNAMIC_LABEL.DIVISION) {
        return this.getDivisionLabel();
      } else if (fieldName === USER_DYNAMIC_LABEL.CLUSTER) {
        return this.getClusterLabel();
      } else if (fieldName === USER_DYNAMIC_LABEL.REGION) {
        return this.getRegionLabel();
      }
  }

  getAllUserDynamicLabels(): Observable<Map<USER_DYNAMIC_LABEL, string>> {
    let division$ = this.getDivisionLabel();
    let cluster$ = this.getClusterLabel();
    let region$ = this.getRegionLabel();

    return combineLatest(([division$, cluster$, region$])).pipe(map(([division, cluster, region]) => {
      const ret = new Map();
      ret.set(USER_DYNAMIC_LABEL.DIVISION, division);
      ret.set(USER_DYNAMIC_LABEL.CLUSTER, cluster);
      ret.set(USER_DYNAMIC_LABEL.REGION, region);
      return ret;
    }));
  }
}

export enum USER_DYNAMIC_LABEL {
  DIVISION = 'Region',
  CLUSTER = 'Cluster',
  REGION = 'Market'
}
